import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "primereact/button";
import "./PanelToolbar.css";

const classNames = (...parts) => parts.filter(Boolean).join(" ");

export const PanelToolbarHost = React.memo(
  ({
    pinned = true,
    toolbar,
    children,
    className = "",
    contentClassName = "",
    contentStyle,
    onMouseLeave,
    ...rest
  }) => {
    const handleMouseLeave = useCallback(
      (event) => {
        onMouseLeave?.(event);

        if (!pinned && event.currentTarget.contains(document.activeElement)) {
          document.activeElement?.blur?.();
        }
      },
      [onMouseLeave, pinned],
    );

    return (
      <div
        className={classNames(
          "panel-toolbar-host",
          pinned
            ? "panel-toolbar-host--pinned"
            : "panel-toolbar-host--unpinned",
          className,
        )}
        onMouseLeave={handleMouseLeave}
        {...rest}
      >
        {!pinned && (
          <div className="panel-toolbar-hover-zone" aria-hidden="true" />
        )}
        {toolbar}
        <div
          className={classNames("panel-toolbar-content", contentClassName)}
          style={contentStyle}
        >
          {children}
        </div>
      </div>
    );
  },
);

PanelToolbarHost.displayName = "PanelToolbarHost";

PanelToolbarHost.propTypes = {
  pinned: PropTypes.bool,
  toolbar: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  contentStyle: PropTypes.object,
  onMouseLeave: PropTypes.func,
};

export const PanelToolbar = React.memo(
  ({
    pinned,
    defaultPinned = true,
    onPinnedChange,
    children,
    right,
    className = "",
    wrap = false,
    pinDataEvoId = "paneltoolbar-btn-pin",
    ...rest
  }) => {
    const [internalPinned, setInternalPinned] = useState(defaultPinned);
    const isControlled = pinned !== undefined;
    const isPinned = isControlled ? pinned : internalPinned;

    const handlePinToggle = useCallback(() => {
      const nextPinned = !isPinned;
      if (!isControlled) setInternalPinned(nextPinned);
      onPinnedChange?.(nextPinned);
    }, [isControlled, isPinned, onPinnedChange]);

    return (
      <div
        className={classNames(
          "panel-toolbar",
          isPinned ? "panel-toolbar--pinned" : "panel-toolbar--unpinned",
          wrap && "panel-toolbar--wrap",
          className,
        )}
        {...rest}
      >
        <div className="panel-toolbar__controls">{children}</div>
        <div className="panel-toolbar__right">
          {right}
          <Button
            icon="pi pi-thumbtack"
            size="small"
            text
            rounded
            className={classNames(
              "panel-toolbar__pin",
              isPinned && "panel-toolbar__pin--pinned",
            )}
            title={isPinned ? "Desafixar toolbar" : "Fixar toolbar"}
            aria-label={isPinned ? "Desafixar toolbar" : "Fixar toolbar"}
            aria-pressed={isPinned}
            onClick={handlePinToggle}
            data-evo-id={pinDataEvoId}
          />
        </div>
      </div>
    );
  },
);

PanelToolbar.displayName = "PanelToolbar";

PanelToolbar.propTypes = {
  pinned: PropTypes.bool,
  defaultPinned: PropTypes.bool,
  onPinnedChange: PropTypes.func,
  children: PropTypes.node,
  right: PropTypes.node,
  className: PropTypes.string,
  wrap: PropTypes.bool,
  pinDataEvoId: PropTypes.string,
};
